/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useCallback, useMemo, useState } from 'react';
import { Link } from '@/components/link';
import type { ShopperSearch } from '@/scapi';
import { cn } from '@/lib/utils';
import { createProductUrl, getImagesForColor } from '@/lib/product/product-utils';
import { useDynamicImageContext } from '@/providers/dynamic-image';
import { ProductImage } from './product-image';
import ImageNavArrows from '@/components/image-nav-arrows';
import { useTranslation } from 'react-i18next';

interface ProductImageContainerProps {
    product: ShopperSearch.schemas['ProductSearchHit'];
    selectedColorValue?: string | null;
    className?: string;
    handleProductClick?: (product: ShopperSearch.schemas['ProductSearchHit']) => void;
    /** Image aspect ratio (width/height). If provided, calculates height based on viewport width. Defaults to 1 (square) */
    imgAspectRatio?: number;
    /** Show prev/next navigation arrows when multiple images are available */
    showNavigationArrows?: boolean;
    showNavigationDots?: boolean;
    navigationVisible?: boolean;
}

const ProductImageContainer = ({
    product,
    selectedColorValue = null,
    className,
    handleProductClick,
    imgAspectRatio = 1,
    showNavigationArrows = false,
    showNavigationDots = false,
    navigationVisible = false,
}: ProductImageContainerProps) => {
    const { t } = useTranslation('product');
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);

    // Get all images for the selected color variant
    const allImages = useMemo(
        () => getImagesForColor(product, selectedColorValue, 'medium'),
        [product, selectedColorValue]
    );

    const currentImage = allImages[selectedImageIndex] ?? allImages[0] ?? product.image;
    const currentImageUrl = currentImage?.disBaseLink || currentImage?.link;
    const imageAltFallback = product.productName || t('imageAlt') || 'Product Image';

    // Report the image URL to the dynamic image context, if available
    const imageContext = useDynamicImageContext();
    currentImageUrl && imageContext?.addSource(currentImageUrl);

    const handleClick = useCallback(() => {
        handleProductClick?.(product);
    }, [handleProductClick, product]);

    // When a non-square aspect ratio is requested, apply it via the native CSS
    // `aspect-ratio` property. The legacy padding-bottom percentage trick is not
    // used here because it conflicts with the native property and collapses the
    // image height to zero.
    const heightStyle = imgAspectRatio !== 1 ? { aspectRatio: `${imgAspectRatio}` } : {};

    return (
        <div
            className={`${showNavigationArrows ? 'group/image ' : ''}relative overflow-hidden bg-secondary/20 flex flex-col ${
                imgAspectRatio === 1 ? 'aspect-square' : ''
            } ${className || ''}`}
            style={heightStyle}>
            {/* Product Image */}
            <Link
                to={createProductUrl(product.productId, selectedColorValue)}
                onClick={handleClick}
                className="block w-full h-full flex-1"
                aria-label={t('viewProductAriaLabel', { productName: imageAltFallback }) || imageAltFallback}>
                <ProductImage
                    key={currentImageUrl || 'product-image'}
                    src={currentImageUrl || ''}
                    alt={currentImage?.alt || imageAltFallback}
                    className="w-full h-full object-cover transition-all duration-300 animate-in fade-in"
                    imageProps={{ className: 'w-full h-full object-cover' }}
                    widths={imageContext?.widths}
                />
            </Link>
            {/* Navigation Arrows - visible on hover */}
            {showNavigationArrows && allImages.length > 1 && (
                <ImageNavArrows
                    imageCount={allImages.length}
                    onIndexChange={setSelectedImageIndex}
                    variant={'circular'}
                    className={cn(
                        'z-20 transition-opacity h-8 w-8',
                        navigationVisible ? 'opacity-100' : 'opacity-0'
                    )}
                />
            )}
            {showNavigationDots && allImages.length > 1 && (
                <div
                    className={cn(
                        'pointer-events-none absolute inset-x-0 bottom-2 z-20 flex justify-center transition-opacity duration-300 product-tile-navigation-dots',
                        navigationVisible ? 'opacity-100' : 'opacity-0'
                    )}
                    role="tablist"
                    aria-label="Product image navigation">
                    <div className="pointer-events-auto flex items-center gap-2">
                        {allImages.map((image, index) => (
                            <button
                                key={`${image.link}-${index}`}
                                type="button"
                                onClick={() => setSelectedImageIndex(index)}
                                className="group/dot py-2 focus:outline-none"
                                role="tab"
                                aria-selected={selectedImageIndex === index}
                                aria-label={`Show product image ${index + 1} of ${allImages.length}`}>
                                <span
                                    className={cn(
                                        'block h-1 w-7 rounded-full transition-colors duration-300 sm:w-10',
                                        selectedImageIndex === index
                                            ? 'bg-[var(--swatch-nav-dot-active)]'
                                            : 'bg-[var(--swatch-nav-dot-inactive)] group-hover/dot:bg-[var(--swatch-nav-dot-hover)]'
                                    )}
                                />
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export { ProductImageContainer };
